import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import type {
  Bid,
  FreelancerBidRestrictions,
  PoolModel,
} from '@freelancer/datastore/collections';
import {
  BidBuyerProjectFee,
  BidRating,
  Enterprise,
  FreelancerReputation,
  IpContract,
  Profile,
  Project,
  ProjectContractSignatures,
  ProjectViewBid,
  ProjectViewUser,
  RECRUITER_RECOMMENDED_SCORE,
  User,
} from '@freelancer/datastore/collections';
import { Feature, FeatureFlagsService } from '@freelancer/feature-flags';
import { ModalService, trackById } from '@freelancer/ui';
import type { QueryParams } from '@freelancer/ui/link';
import { LinkUnderline } from '@freelancer/ui/link';
import { ModalSize } from '@freelancer/ui/modal';
import { RibbonColor, RibbonPlacement } from '@freelancer/ui/ribbon';
import {
  FontStyle,
  FontType,
  FontWeight,
  ReadMore,
  TextSize,
} from '@freelancer/ui/text';
import { UpgradeType } from '@freelancer/ui/upgrade-tag';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import type { UsernameBadges } from '@freelancer/ui/username';
import { UsernameSize } from '@freelancer/ui/username';
import { isDefined } from '@freelancer/utils';
import {
  BidAwardStatusApi,
  ProjectStatusApi,
  ProjectTypeApi,
} from 'api-typings/projects/projects';
import { CorporateStatusApi } from 'api-typings/users/users';
import type { Observable, Subscription } from 'rxjs';
import { ReplaySubject } from 'rxjs';
import { filter, map, startWith, take } from 'rxjs/operators';
import { DynamicBidTags, RibbonType } from './bid-ribbon.types';

@Component({
  selector: 'fl-bid-pattern-card-additional-details',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BidPatternCardAdditionalDetailsComponent {}
@Component({
  selector: 'fl-bid-pattern-card-actions-notes',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BidPatternCardActionsNotesComponent {}
@Component({
  selector: 'fl-bid-pattern-card-actions',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BidPatternCardActionsComponent {}

@Component({
  selector: 'fl-bid-pattern-card',
  template: `
    <ng-container>
      <div #bidCard>
        <div
          class="BidCardHeader"
          [flMarginBottomDesktop]="'xxsmall'"
        >
          <div
            class="UserInfoContainer"
            [flMarginBottom]="'xsmall'"
            [flMarginBottomDesktop]="'none'"
          >
            <ng-container
              *ngIf="!videoSrc && !showOnboardingModal; else avatar"
            >
              <fl-link
                [link]="
                  biddersMultiProfile
                    ? '/u/' + bidder.username + '/id/' + biddersMultiProfile.id
                    : '/u/' + bidder.username
                "
                [underline]="LinkUnderline.NEVER"
                [queryParams]="userLinkQueryParams"
              >
                <ng-container [ngTemplateOutlet]="avatar"></ng-container>
              </fl-link>
            </ng-container>
            <ng-template #avatar>
              <fl-user-avatar
                flTrackingSection="BidCard"
                flTrackingLabel="{{
                  videoSrc === undefined
                    ? 'UserAvatarClick'
                    : isEmployerView
                      ? 'UserAvatarVideoClientClick'
                      : 'UserAvatarVideoFreelancerClick'
                }}"
                [flTrackingReferenceType]="'bid_id'"
                [flTrackingReferenceId]="bid.id"
                [flMarginRight]="'small'"
                [size]="AvatarSize.LARGE"
                [sizeDesktop]="AvatarSize.XLARGE"
                [users]="[bidder]"
                [isVideoAvatar]="videoSrc !== undefined"
                [showOnboardingModal]="showOnboardingModal"
                [videoSrc]="videoSrc"
                (videoModalVideoPlayed)="handleVideoModalVideoPlayed()"
                (videoModalVideoEnded)="handleVideoModalVideoEnded()"
              ></fl-user-avatar>
            </ng-template>
            <div>
              <fl-user-info
                class="UserInfo"
                [flMarginBottomDesktop]="'xxsmall'"
                [usernameMarginBottomDesktop]="'xxsmall'"
                [user]="bidder"
                [userLink]="
                  biddersMultiProfile
                    ? '/u/' + bidder.username + '/id/' + biddersMultiProfile?.id
                    : '/u/' + bidder.username
                "
                [userLinkQueryParams]="userLinkQueryParams"
                [userBadges]="userBadges"
                [ratingControl]="ratingControl"
                [freelancerReputation]="reputation"
                [usernameSize]="UsernameSize.LARGE"
                [usernameSizeDesktop]="UsernameSize.XLARGE"
                [showCity]="showCity"
                [showEarningScore]="
                  (bidCardSettings$ | flAsync)?.bidCardEarnings
                "
                [showCountry]="(bidCardSettings$ | flAsync)?.bidCardCountry"
                [isDeloitteProject]="isDeloitteProject"
                [isTokenProject]="isTokenProject"
                [isBidder]="isBidder"
                [showNewFreelancerLabel]="showNewFreelancerLabel"
              >
              </fl-user-info>
              <fl-text
                [flHideMobile]="true"
                [fontType]="FontType.PARAGRAPH"
                [size]="TextSize.XSMALL"
                [weight]="FontWeight.BOLD"
              >
                {{ biddersMultiProfile?.tagline ?? bidder.tagLine }}
              </fl-text>
            </div>
          </div>
          <div class="PriceContainer">
            <div
              *ngIf="showBidAmountAndDescription && !isTokenProject"
              [ngClass]="{
                PaymentFeeRate: showPaymentFeeRate$ | flAsync,
                PriceRate: !(showPaymentFeeRate$ | flAsync),
              }"
              [flMarginBottom]="'xxsmall'"
              [flMarginBottomDesktop]="'none'"
            >
              <fl-text
                [fontType]="FontType.PARAGRAPH"
                [flMarginRight]="'xxsmall'"
                [flMarginRightDesktop]="'none'"
                [matTooltip]="priceTooltip ?? ''"
                [matTooltipPosition]="'above'"
                [size]="TextSize.LARGE"
                [weight]="FontWeight.BOLD"
              >
                {{ bid.amount | flCurrency: project.currency.code }}
              </fl-text>
              <!-- LoadShift Employer bid total fees -->
              <ng-container
                *ngIf="
                  (isEmployerView || isCollaboratorView) &&
                    (bidCardSettings$ | flAsync)?.bidTotalFee;
                  else defaultView
                "
              >
                <p
                  class="text-xsmall"
                  *ngIf="
                    salesTaxEnabled && bid.salesTax && bid.salesTax.taxType
                  "
                  matTooltip="3.3% project fee incl. GST"
                  [matTooltipPosition]="'below'"
                  i18n-matTooltip="Projectfee tool tip"
                  i18n="Including tax type amount label"
                >
                  {{
                    totalFee
                      | flCurrency
                        : project.currency.code
                        : { disableCode: true }
                  }}
                  incl. fees
                </p>
              </ng-container>
              <ng-template #defaultView>
                <p
                  class="text-xsmall"
                  *ngIf="
                    salesTaxEnabled && bid.salesTax && bid.salesTax.taxType
                  "
                  i18n="Including tax type amount label"
                  [flMarginRight]="'xxsmall'"
                  [flMarginRightDesktop]="'none'"
                >
                  Including {{ bid.salesTax.taxType }}
                </p>
              </ng-template>
              <p
                class="text-xsmall"
                i18n="Freelancer rate per hour"
                [flMarginRight]="'xxsmall'"
                [flMarginRightDesktop]="'none'"
                [flMarginBottomDesktop]="'xxsmall'"
              >
                <!-- Hourly Project label -->
                <ng-container *ngIf="bid.amount && isProjectHourly">
                  per hour
                </ng-container>

                <!-- Fixed Price Project label -->
                <ng-container *flFeature="Feature.PROJECT_DELIVER_PERIOD">
                  <ng-container *ngIf="bid.amount && !isProjectHourly">
                    <ng-container
                      *ngTemplateOutlet="fixedBidAmountLabel"
                    ></ng-container>
                  </ng-container>
                </ng-container>
              </p>

              <!-- Ribbons -->
              <ng-container [ngSwitch]="ribbon">
                <ng-container *ngSwitchCase="RibbonType.RECOMMENDED">
                  <div class="Ribbon">
                    <fl-ribbon
                      *ngIf="agent"
                      i18n="Recommended bid"
                      i18n-matTooltip="Recommended bid tooltip"
                      matTooltip="We've recommended a freelancer for you based on the experience and ratings of each freelancer on similar project types."
                      [matTooltipPosition]="'above'"
                      [matTooltipDisabled]="
                        !(isEmployerView && isDeloitteExternalBid)
                      "
                      [color]="RibbonColor.RECRUITER"
                      [placement]="RibbonPlacement.RIGHT"
                    >
                      Recommended by {{ agent.displayName }}
                    </fl-ribbon>
                    <fl-ribbon
                      *ngIf="!agent"
                      i18n="Recommended bid"
                      i18n-matTooltip="Recommended bid tooltip"
                      matTooltip="We've recommended a freelancer for you based on the experience and ratings of each freelancer on similar project types."
                      [matTooltipPosition]="'above'"
                      [matTooltipDisabled]="
                        !(isEmployerView && isDeloitteExternalBid)
                      "
                      [color]="RibbonColor.PRIMARY"
                      [placement]="RibbonPlacement.RIGHT"
                    >
                      Recommended
                    </fl-ribbon>
                  </div>
                </ng-container>
                <ng-container *ngSwitchCase="RibbonType.SPONSORED">
                  <div class="Ribbon">
                    <fl-ribbon
                      i18n="Sponsored bid"
                      [color]="RibbonColor.SECONDARY"
                      [placement]="RibbonPlacement.RIGHT"
                    >
                      Sponsored
                    </fl-ribbon>
                  </div>
                </ng-container>
                <ng-container *ngSwitchCase="RibbonType.DYNAMIC">
                  <div class="Ribbon">
                    <fl-ribbon
                      [color]="RibbonColor.SECONDARY"
                      [placement]="RibbonPlacement.RIGHT"
                    >
                      {{ dynamicBidTag }}
                    </fl-ribbon>
                  </div>
                </ng-container>
              </ng-container>
            </div>

            <ng-template #fixedBidAmountLabel>
              <ng-container i18n="Fixed project bid amount label">
                in {{ bid.period }}
                <ng-container *ngIf="bid.period === 1"> day </ng-container>
                <ng-container *ngIf="bid.period !== 1"> days </ng-container>
              </ng-container>
            </ng-template>
            <p
              class="text-xs font-bold Tagline"
              [flShowMobile]="true"
              [flHideTablet]="true"
              [flMarginBottom]="'xxsmall'"
              [flMarginBottomTablet]="'none'"
            >
              {{ biddersMultiProfile?.tagline ?? bidder.tagLine }}
            </p>
          </div>
        </div>

        <div class="BidCardBody">
          <div
            class="DescriptionContainer"
            [flMarginRightDesktop]="'small'"
          >
            <fl-text
              [displayLineBreaks]="displayLineBreaks"
              [fontType]="FontType.PARAGRAPH"
              [flMarginBottom]="'xsmall'"
              [flMarginBottomDesktop]="
                showShowcases && (hasPortfolioOnBidCardQueryParam$ | flAsync)
                  ? 'xsmall'
                  : 'none'
              "
              [maxLines]="4"
              [readMore]="ReadMore.LINK"
              (expand)="handleExpand($event)"
            >
              <ng-container *ngIf="bid.description; else noBidDescription">
                {{ bid.description }}
              </ng-container>
              <ng-template #noBidDescription>
                <ng-container
                  *ngIf="!showSealedTag"
                  i18n="Bid description"
                >
                  No proposal submitted.
                </ng-container>
              </ng-template>
            </fl-text>
            <div
              *ngIf="
                showShowcases && (hasPortfolioOnBidCardQueryParam$ | flAsync)
              "
              class="BidCardShowcaseThumbnailContainer"
              [flMarginBottom]="'xsmall'"
              [flMarginBottomDesktop]="'none'"
              [flTrackingSection]="'PortfolioOnBidCard'"
            >
              <fl-showcase-thumbnail
                *ngFor="
                  let showcaseItem of bid.showcases;
                  let i = index;
                  trackBy: trackById
                "
                class="BidCardShowcaseThumbnailContainer-item"
                matTooltip="View portfolio"
                i18n-matTooltip="View portfolio tooltip"
                [flTrackingLabel]="'ShowcaseThumbnailClick'"
                [flTrackingReferenceId]="showcaseItem.id"
                [flTrackingReferenceType]="'bid_showcase_item_id'"
                [matTooltipPosition]="'below'"
                [matTooltipTouchGestures]="'off'"
                [showcase]="showcaseItem"
                (click)="openShowcaseModal(i)"
              ></fl-showcase-thumbnail>
            </div>
            <div
              *ngIf="bidPatternCardActionsNotesComponent"
              class="BidCardFooter"
            >
              <ng-content
                select="fl-bid-pattern-card-actions-notes"
              ></ng-content>
            </div>
            <div class="TagNdaDetailsContainer">
              <div *ngIf="(bidCardSettings$ | flAsync)?.bidsSealedPill">
                <div
                  *ngIf="showSealedTag"
                  class="TagContainer"
                >
                  <ng-container *ngIf="isUsersBid">
                    <fl-upgrade-tag
                      i18n-matTooltip="Sealed bid description tooltip"
                      matTooltip="Your bid is hidden from other freelancers as it has a sealed upgrade. This prevents others from copying your bid, making it more unique."
                      [matTooltipPosition]="'above'"
                      [upgradeType]="UpgradeType.SEALED"
                      [flMarginBottom]="'xsmall'"
                      [flMarginBottomDesktop]="'none'"
                      [flMarginRight]="'xsmall'"
                    ></fl-upgrade-tag>
                  </ng-container>
                  <ng-container *ngIf="!isUsersBid">
                    <fl-upgrade-tag
                      i18n-matTooltip="Sealed bid description tooltip"
                      matTooltip="The sealed upgrade hides bids from other freelancers. This prevents others from copying bids, making them more unique."
                      [matTooltipPosition]="'above'"
                      [flMarginBottom]="'xsmall'"
                      [flMarginBottomDesktop]="'none'"
                      [flMarginRight]="'xsmall'"
                      [upgradeType]="UpgradeType.SEALED"
                    ></fl-upgrade-tag>
                  </ng-container>
                </div>
              </div>
              <div class="AdditionalDetailsContainer">
                <ng-content
                  select="fl-bid-pattern-card-additional-details"
                ></ng-content>
              </div>
            </div>
          </div>

          <div class="CtaActionContainer">
            <ng-content select="fl-bid-pattern-card-actions"></ng-content>
          </div>
        </div>
      </div>
    </ng-container>
  `,
  styleUrls: ['./bid-pattern-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BidPatternCardComponent
  implements AfterViewInit, OnInit, OnChanges, OnDestroy
{
  AvatarSize = AvatarSize;
  Feature = Feature;
  FontType = FontType;
  FontWeight = FontWeight;
  FontStyle = FontStyle;
  LinkUnderline = LinkUnderline;
  ReadMore = ReadMore;
  RibbonColor = RibbonColor;
  RibbonPlacement = RibbonPlacement;
  RibbonType = RibbonType;
  TextSize = TextSize;
  UpgradeType = UpgradeType;
  UsernameSize = UsernameSize;
  trackById = trackById;

  isBidAwardedAndAccepted: boolean;
  isBidder? = true;
  isDeloitteProject: boolean;
  isInsourceProject: boolean;
  isProjectHourly: boolean;
  isProjectOpen: boolean;
  isTokenProject: boolean;
  ribbon: RibbonType | undefined;
  showBidAmountAndDescription = true;
  showNewFreelancerLabel = true;
  showMoreOptions: boolean;
  showSealedTag = false;
  showShowcases = false;

  displayLineBreaks = false;
  userBadges: UsernameBadges;
  userLinkQueryParams: QueryParams;

  ratingControl = new FormControl(0, { nonNullable: true });

  @Input() agent?: User;
  @Input() bid: ProjectViewBid;
  @Input() bidder: ProjectViewUser;
  @Input() biddersMultiProfile?: Profile;
  @Input() clientAgreement?: ProjectContractSignatures;
  @Input() dynamicBidTag?: DynamicBidTags;
  @Input() employer: ProjectViewUser;
  @Input() hasBidAwardPermission: boolean;
  @Input() hasChatPermission: boolean;
  @Input() insourceEnabledPools: readonly PoolModel[] | undefined;
  @Input() ipContract?: IpContract;
  @Input() isCollaboratorView: boolean;
  @Input() isCorporateUser: boolean;
  @Input() isDeloitteExternalBid: boolean;
  @Input() isEmployerView: boolean;
  @Input() isUsersBid: boolean;
  @Input() project: Project;
  @Input() projectFee: BidBuyerProjectFee;
  @Input() rating?: BidRating;
  @Input() reputation?: FreelancerReputation;
  @Input() salesTaxEnabled?: boolean;
  @Input() showCity?: boolean;
  @Input() showOnboardingModal?: boolean;
  @Input() talentNetworkPools: readonly PoolModel[] | undefined;
  @Input() userId: number;
  @Input() videoSrc?: string;

  @Output() clientSeeBid = new EventEmitter<Bid>();
  @Output() videoModalVideoPlayed = new EventEmitter<void>();
  @Output() videoModalVideoEnded = new EventEmitter<void>();

  showEditButton$: Observable<boolean>;
  showEarningScore$: Observable<boolean>;
  showCountry$: Observable<boolean>;
  bidCardSettings$: Observable<
    | {
        readonly bidCardCountry: boolean;
        readonly bidCardEarnings: boolean;
        readonly bidsSealedPill: boolean;
        readonly bidTotalFee: boolean;
      }
    | false
  >;

  freelancerBidRestrictions$: Observable<FreelancerBidRestrictions>;
  priceTooltip?: string;
  totalFee: number;
  showPaymentFeeRate$ = this.featureFlagService.getConfig('bidCard').pipe(
    map(settings => {
      const bidTotalFee =
        settings && typeof settings === 'object' ? settings.bidTotalFee : false;
      return !!bidTotalFee && (this.isEmployerView || this.isCollaboratorView);
    }),
  );

  @ContentChild(BidPatternCardActionsNotesComponent)
  bidPatternCardActionsNotesComponent: BidPatternCardActionsNotesComponent;

  @ViewChildren('bidCard', { read: ElementRef })
  bidCard: QueryList<ElementRef>;

  private _isUsersBidSubject$ = new ReplaySubject<boolean>(1);
  isUsersBid$: Observable<boolean> = this._isUsersBidSubject$.asObservable();

  private bidCardSubscription?: Subscription;

  bidSeenObserver: IntersectionObserver;
  multiProfileId: number | undefined;

  hasPortfolioOnBidCardQueryParam$: Observable<boolean>;

  constructor(
    private featureFlagService: FeatureFlagsService,
    private modalService: ModalService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.bidCardSettings$ = this.featureFlagService.getConfig('bidCard');

    this.userBadges = {
      verified: this.bidder.status.freelancerVerifiedUser,
      corporate:
        this.bidder.corporate &&
        this.bidder.corporate.status === CorporateStatusApi.ACTIVE,
      preferred_freelancer: this.bidder.preferredFreelancer,
      shareholder: this.bidder.isShareholder,
      rising_star: this.bidder.isRisingStar,
    };

    const isExternalBidder = this.talentNetworkPools?.some(
      pool =>
        this.project.poolIds?.includes(pool.id) &&
        this.bidder.poolIds?.includes(pool.id),
    );

    const isInternalBidder = this.insourceEnabledPools?.some(
      pool =>
        this.project.poolIds?.includes(pool.id) &&
        this.bidder.poolIds?.includes(pool.id),
    );

    this.showNewFreelancerLabel = !(isExternalBidder || isInternalBidder);

    this.priceTooltip = this.salesTaxEnabled
      ? undefined
      : $localize`Price includes tax`;

    this.userLinkQueryParams = {
      projectId: this.project.id,
      ref_bid_id: this.bid.id,
      ref_project_id: this.project.id,
    };

    this.totalFee = this.bid.amount + (this.projectFee?.amount ?? 0);

    this.hasPortfolioOnBidCardQueryParam$ = this.route.queryParamMap.pipe(
      map(params => params.has('showPortfolioOnBidCard')),
    );
  }

  ngAfterViewInit(): void {
    this.bidCardSubscription = this.bidCard.changes
      .pipe(
        startWith(this.bidCard),
        map(el => el && el.first),
        filter(isDefined),
        take(1),
      )
      .subscribe(el => {
        if (this.isEmployerView) {
          this.bidSeenObserver = new IntersectionObserver(
            ([entry], observer) => {
              if (entry.intersectionRatio === 1) {
                this.clientSeeBid.emit(this.bid);
                observer.unobserve(el.nativeElement);
              }
            },
            {
              rootMargin: '0px',
              threshold: 1.0,
            },
          );
          this.bidSeenObserver.observe(el.nativeElement);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.showSealedTag = false;
    this.showShowcases = this.bid.showcases
      ? this.bid.showcases.length > 0
      : false;
    this.showBidAmountAndDescription = true;
    if (this.bidder) {
      if (!this.isUsersBid && this.bidder.isNewFreelancer) {
        this.isBidder = false;
      }
      this.showSealedTag = this.bid.sealed || false;
      if (this.isEmployerView || this.isCollaboratorView) {
        // if recruiter upgrade is enabled,
        // we only show recommendation for recruiter selected bid
        // which has bid.score === 99

        // If we have a dynamic bid tag, we use it instead,
        if (this.dynamicBidTag && this.dynamicBidTag !== DynamicBidTags.NONE) {
          this.ribbon = RibbonType.DYNAMIC;
        } else {
          const recommended = this.project.upgrades.assisted
            ? this.bid.score === RECRUITER_RECOMMENDED_SCORE
            : this.bid.recommended;

          this.ribbon = recommended
            ? RibbonType.RECOMMENDED
            : this.bid.sponsored
              ? RibbonType.SPONSORED
              : undefined;
        }

        this.isBidder = false;
      } else {
        this.ribbon = this.bid.sponsored ? RibbonType.SPONSORED : undefined;
        this.showBidAmountAndDescription = this.showSealedTag
          ? this.isUsersBid
          : true;
      }

      this.ratingControl.setValue(
        (this.bidder.reputation &&
          this.bidder.reputation.entireHistory.overall) ||
          0,
      );
    }

    if (this.bid && 'bid' in changes) {
      this.isBidAwardedAndAccepted =
        this.bid.awardStatus === BidAwardStatusApi.AWARDED;
    }

    if (this.project && 'project' in changes) {
      this.isProjectHourly = this.project.type === ProjectTypeApi.HOURLY;
      this.isProjectOpen = this.project.status === ProjectStatusApi.ACTIVE;
      this.isInsourceProject = this.project.isInsourceProject;
      this.isTokenProject = this.project.isTokenProject;
      this.isDeloitteProject =
        !!this.project.enterpriseIds &&
        this.project.enterpriseIds.includes(Enterprise.DELOITTE_DC);
    }

    if (this.isUsersBid && 'isUsersBid' in changes) {
      this._isUsersBidSubject$.next(this.isUsersBid);
    }

    if ('biddersMultiProfile' in changes) {
      this.multiProfileId = changes.biddersMultiProfile.currentValue?.id;
    }

    this.showMoreOptions =
      !this.isEmployerView &&
      !this.isCollaboratorView &&
      !this.isUsersBid &&
      this.isProjectOpen &&
      !this.showSealedTag;

    this.totalFee = this.bid.amount + (this.projectFee?.amount ?? 0);
  }

  ngOnDestroy(): void {
    if (this.bidSeenObserver) {
      this.bidSeenObserver.disconnect();
    }

    if (this.bidCardSubscription) {
      this.bidCardSubscription.unsubscribe();
    }
  }

  handleExpand(isExpanded: boolean): void {
    this.displayLineBreaks = isExpanded;
  }

  handleVideoModalVideoPlayed(): void {
    this.videoModalVideoPlayed.emit();
  }

  handleVideoModalVideoEnded(): void {
    this.videoModalVideoEnded.emit();
  }

  openShowcaseModal(index: number): void {
    this.modalService.open('ShowcaseViewerModal', {
      inputs: {
        bid: this.bid,
        bidder: this.bidder,
        initialIndex: index,
        employer: this.employer,
        hasBidAwardPermission: this.hasBidAwardPermission,
        hasChatPermission: this.hasChatPermission,
        isCollaboratorView: this.isCollaboratorView,
        isEmployerView: this.isEmployerView,
        project: this.project,
        showcaseIds: this.bid.showcases.map(({ showcaseId }) => showcaseId),
        userId: this.userId,
      },
      edgeToEdge: true,
      customViewHeader: true,
      mobileFullscreen: true,
      size: ModalSize.XLARGE,
    });
  }
}
